import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import { reportFailedResponse } from "../client-error-reporting.ts"
import { apiUrl } from "../runtime-origin.ts"
import type { IEnvelopeResult } from "./errors.ts"
import { extractErrorMessage, unwrapData } from "./errors.ts"

export type TMaterialKind = "capture" | "archive"
export type TMaterialOrigin = "camera" | "upload"
export type TMaterialStatus = "stored" | "extracting" | "ready" | "failed"

export interface IMaterialInterpretation {
  kind: "mermaid"
  sourceMaterialId: string
  body: string
}

export interface IMeetingMaterial {
  id: string
  meetingId: string
  kind: TMaterialKind
  origin: TMaterialOrigin
  capturedAtSeconds: number | null
  filename: string
  mime: string
  byteSize: number
  status: TMaterialStatus
  extractedKind: string | null
  classification: string | null
  extractedText: string | null
  interpretation: IMaterialInterpretation | null
  includedInAnalysisAt: string | null
  createdAt: string
  fileUrl: string
}

export interface IMaterialsList {
  items: IMeetingMaterial[]
  unusedCount: number
}

export async function fetchMeetingMaterials(company: CompanyApi, meetingId: string): Promise<IMaterialsList> {
  const result = (await company.meetings({ id: meetingId }).materials.get()) as IEnvelopeResult<IMaterialsList>
  return unwrapData(result)
}

export async function uploadMeetingMaterial(
  meetingId: string,
  file: File,
  opts?: { kind?: TMaterialKind; origin?: TMaterialOrigin; capturedAtSeconds?: number | null },
): Promise<IMeetingMaterial> {
  const form = new FormData()
  form.append("file", file)
  if (opts?.kind) form.append("kind", opts.kind)
  if (opts?.origin) form.append("origin", opts.origin)
  if (opts?.capturedAtSeconds != null) form.append("capturedAtSeconds", String(opts.capturedAtSeconds))
  const companyId = window.__activeOrgId
  const res = await fetch(apiUrl(`/api/companies/${companyId}/meetings/${meetingId}/materials`), {
    method: "POST",
    body: form,
    credentials: "include",
  })
  if (!res.ok) {
    // Surface the server's actual message (e.g. "That file type is not allowed…")
    // instead of a bare status — the raw fetch must not swallow it into a generic
    // "Upload failed: 400". Backend errors are the nested `{ error: { message } }` shape.
    let message = `Upload failed (${res.status})`
    try {
      const body: unknown = await res.json()
      message = extractErrorMessage(body)
      reportFailedResponse(res, body)
    } catch {
      reportFailedResponse(res)
    }
    throw new Error(message)
  }
  const json = (await res.json()) as IEnvelopeResult<IMeetingMaterial>
  return unwrapData(json)
}

export async function deleteMeetingMaterial(company: CompanyApi, meetingId: string, materialId: string): Promise<void> {
  const result = (await company.meetings({ id: meetingId }).materials({ materialId }).delete()) as IEnvelopeResult<{
    success: boolean
  }>
  unwrapData(result)
}

export async function digitizeMeetingMaterial(
  company: CompanyApi,
  meetingId: string,
  materialId: string,
): Promise<IMeetingMaterial> {
  const result = (await company
    .meetings({ id: meetingId })
    .materials({ materialId })
    .digitize.post({})) as IEnvelopeResult<IMeetingMaterial>
  return unwrapData(result)
}

export async function reanalyzeMeetingMaterials(company: CompanyApi, meetingId: string): Promise<void> {
  await company.meetings({ id: meetingId }).materials.reanalyze.post({})
}
